import { CommentThreadAnchorType, CreditAmount, DocumentCommentContext, MessageType, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
// This api is used to create messages in the context of the service.  It separates out the data operations from
// the coordination, and provides a really high level CRUD interface to callers that abstracts over details and takes
// care of all of the integrity concerns between users, threads, and messages.
class MessageCreationApi {
    user;
    commentThread;
    constructor(user, commentThread) {
        this.user = user;
        this.commentThread = commentThread;
    }
    createUserMessage(content) {
        return {
            uid: guid(),
            owner_uid: this.user.uid,
            owner_handle: this.user.handle,
            owner_full_name: this.user.full_name,
            belongs_to_comment_thread_uid: this.commentThread.uid,
            created_at: Date.now(),
            updated_at: Date.now(),
            content,
        };
    }
    // Create a brand new copilot message that can be written to by future actions.
    // TODO: interesting to imagine how we could constrain access to this where the user is known to be copilot
    createCopilotMessage(content, messageOverrides) {
        const commentUid = guid();
        return {
            uid: commentUid,
            owner_uid: this.user.uid,
            belongs_to_comment_thread_uid: this.commentThread.uid,
            created_at: new Date().getTime(),
            updated_at: new Date().getTime(),
            content,
            owner_handle: this.user.handle,
            owner_full_name: "Flux",
            type: MessageType.mutableCopilotStatus,
            ...messageOverrides,
        };
    }
    createCopilotBlockedMessage(reason, creditPeriod, creditAccountUid, creditAccountHadLowBalance, respondedTo) {
        const commentUid = guid();
        return {
            uid: commentUid,
            owner_uid: this.user.uid,
            belongs_to_comment_thread_uid: this.commentThread.uid,
            created_at: new Date().getTime(),
            updated_at: new Date().getTime(),
            content: "Copilot could not be used",
            owner_handle: this.user.handle,
            owner_full_name: "Flux",
            type: MessageType.copilotBlocked,
            copilotMetadata: {
                respondedTo,
                traceId: guid(),
                totalCreditsUsed: CreditAmount.parse(0),
                creditPeriod,
                creditAccountUid,
                creditAccountHadLowBalance,
                blockedReason: reason,
            },
        };
    }
}
class MessageUpdateApi {
    // Update a message with a copilot expert
    setCopilotExpert(message, expert) {
        return {
            ...message,
            copilotRuntimeConfiguration: {
                type: "copilotRuntimeConfiguration",
                expert: {
                    expert,
                },
            },
        };
    }
    // Update a message such that it remains writable
    updateCopilotMessageWithStatus(message, status, copilotMetadata) {
        return {
            ...message,
            updated_at: new Date().getTime(),
            content: status,
            copilotMetadata,
        };
    }
    // Set a message so that it contains an error.  This is now a final message and cannot be mutated in the future.
    updateCopilotMessageWithError(message, error, copilotMetadata) {
        return {
            ...message,
            updated_at: new Date().getTime(),
            type: MessageType.copilotError,
            content: error,
            copilotMetadata,
        };
    }
    // Send a final success message.
    finalizeCopilotMessage(message, content, copilotMetadata) {
        return {
            ...message,
            updated_at: new Date().getTime(),
            type: MessageType.finalCopilotStatus,
            content,
            copilotMetadata,
        };
    }
}
// Service for interacting with message (comment) data at a high level.
export class MessageService {
    commentRepository;
    constructor(commentRepository) {
        this.commentRepository = commentRepository;
    }
    // Get all of the messages for the reply thread that a message belongs to.
    // This will work for a message at any level of a reply tree.  Behavior:
    // - Calling this function on a top level message with no replies will return an array containing only that message.
    // - Calling this function on a top level message with replies will return all reply messages, plus the top level messages.
    // - Calling this function on a reply message will return all sibling replies both before and after this one, plus the top level message.
    async getReplyThreadMessages(documentUid, message) {
        const threadForMessage = await this.commentRepository.getThread(documentUid, message.belongs_to_comment_thread_uid);
        // If it's a reply thread, get all of the messages that are part of it.  Otherwise, there aren't any.
        if (threadForMessage && threadForMessage.anchor.type === CommentThreadAnchorType.reply) {
            const parentMessage = await this.commentRepository.get(documentUid, threadForMessage.anchor.reply_to_comment_uid);
            if (parentMessage == null) {
                throw new Error(`invariant violated: reply anchor for thread ${threadForMessage.uid} references nonexistent comment ${documentUid}:${threadForMessage.anchor.reply_to_comment_uid}`);
            }
            const allReplies = await this.commentRepository.getAllForThread(documentUid, message.belongs_to_comment_thread_uid);
            return { parentMessage, replyMessages: [parentMessage, ...allReplies] };
        }
        // Find out if there are replies to this message.
        else {
            const replyThreadToMessage = await this.commentRepository.getReplyThread(documentUid, message.uid);
            if (replyThreadToMessage != null) {
                return {
                    parentMessage: message,
                    replyMessages: await this.commentRepository.getAllForThread(documentUid, replyThreadToMessage.uid),
                };
            }
            return { parentMessage: message, replyMessages: [] };
        }
    }
    async updateMessage(documentUid, updater) {
        const newMessage = updater(new MessageUpdateApi());
        await this.commentRepository.save(documentUid, newMessage);
        return newMessage;
    }
    // Create a new comment on schematic.
    async createDocumentSchematicComment(documentUid, creatingUser, position, creator) {
        const anchor = {
            type: CommentThreadAnchorType.position,
            position,
        };
        const newThread = await this.newThreadWithAnchor(documentUid, creatingUser, anchor);
        const newComment = creator(new MessageCreationApi(creatingUser, newThread));
        await this.commentRepository.saveThread(documentUid, newThread);
        await this.commentRepository.save(documentUid, newComment);
        return newComment;
    }
    // Create a new comment on the 2d pcb view.
    async createDocumentPcb2DComment(documentUid, creatingUser, position, creator) {
        const anchor = {
            type: CommentThreadAnchorType.pcb2d_position,
            position,
        };
        const newThread = await this.newThreadWithAnchor(documentUid, creatingUser, anchor);
        const newComment = creator(new MessageCreationApi(creatingUser, newThread));
        await this.commentRepository.saveThread(documentUid, newThread);
        await this.commentRepository.save(documentUid, newComment);
        return newComment;
    }
    // Create a new comment in project chat.
    // NOTE: If the project chat thread does not exist for the document, this will fail.
    async createProjectChatMessage(documentUid, creatingUser, creator) {
        const projectChatThread = await this.commentRepository.getProjectChatThread(documentUid);
        if (projectChatThread == null) {
            // TODO: should we actually support a get-or-create here?
            throw new Error("project chat thread does not exist for document");
        }
        const newMessage = creator(new MessageCreationApi(creatingUser, projectChatThread));
        await this.commentRepository.save(documentUid, newMessage);
        return newMessage;
    }
    // Create a reply to any message.
    async replyToMessage(documentUid, replyingUser, originalMessage, creator) {
        // If the message already belongs to a reply thread, we're just adding to that existing thread.  Otherwise,
        // we need to create one. Note that this means more than one request/response may occur in the same reply thread
        const replyThread = await this.getOrCreateReplyThreadForMessage(documentUid, replyingUser, originalMessage);
        const replyMessage = creator(new MessageCreationApi(replyingUser, replyThread));
        await this.commentRepository.save(documentUid, replyMessage);
        return replyMessage;
    }
    // Get the project chat thread for a document, creating it if it doesn't exist.
    // NOTE: We have some slightly special handling in here for project chat threads - they're not the same as other threads
    // because their reference numbers don't contribute to the max in the same way others do.
    async getOrCreateProjectChatThread(documentUid, documentOwner) {
        const projectChatThread = await this.commentRepository.getProjectChatThread(documentUid);
        if (projectChatThread != null) {
            return { projectChatThread, didCreate: false };
        }
        const projectChatAnchor = {
            type: CommentThreadAnchorType.document,
            document_uid: documentUid,
            context: DocumentCommentContext.projectChat,
        };
        const newThread = {
            anchor: projectChatAnchor,
            created_at: Date.now(),
            updated_at: Date.now(),
            owner_uid: documentOwner.uid,
            reference: -1,
            resolved: false,
            uid: guid(),
        };
        await this.commentRepository.saveThread(documentUid, newThread);
        return {
            projectChatThread: newThread,
            didCreate: true,
        };
    }
    // This just creates a new anchor that respects the existing reference numbers.
    async newThreadWithAnchor(documentUid, creatingUser, anchor) {
        const allThreads = await this.commentRepository.getThreads(documentUid);
        const maxReferenceNo = allThreads.reduce((currentMax, thread) => {
            return thread.reference > currentMax ? thread.reference : currentMax;
        }, 0);
        const newThread = {
            uid: guid(),
            anchor,
            reference: maxReferenceNo + 1,
            resolved: false,
            owner_uid: creatingUser.uid,
            created_at: Date.now(),
            updated_at: Date.now(),
        };
        return newThread;
    }
    // If a message already has a reply thread (i.e. it belongs to a reply thread or it has a reply thread), return that thread.
    // Otherwise, create a new reply thread for the message and return it.
    async getOrCreateReplyThreadForMessage(documentUid, replyingUser, message) {
        const existingReplyThread = await this.commentRepository.getReplyThread(documentUid, message.uid);
        if (existingReplyThread != null)
            return existingReplyThread;
        const existingThread = await this.commentRepository.getThread(documentUid, message.belongs_to_comment_thread_uid);
        if (!existingThread) {
            throw new Error(`invariant violated: orphaned comment ${documentUid}:${message.uid} references a parent thread ${message.belongs_to_comment_thread_uid} that does not exist!`);
        }
        if (existingThread.anchor.type === CommentThreadAnchorType.reply) {
            return existingThread;
        }
        const newReplyThreadAnchor = {
            type: CommentThreadAnchorType.reply,
            parent_thread_uid: message.belongs_to_comment_thread_uid,
            reply_to_comment_uid: message.uid,
        };
        const newThread = {
            anchor: newReplyThreadAnchor,
            reference: -1,
            resolved: false,
            uid: guid(),
            owner_uid: replyingUser.uid,
            created_at: Date.now(),
            updated_at: Date.now(),
        };
        await this.commentRepository.saveThread(documentUid, newThread);
        return newThread;
    }
}
//# sourceMappingURL=service.js.map